<template>
  <div class="newbie-lesson">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <div class="filter-card">
        <div class="filter-bar">
          <div class="hint">新手课堂：上传封面图与课件（PDF / Word）</div>
          <div class="action-group">
            <el-button type="primary" icon="el-icon-plus" @click="openCreate">新增课程</el-button>
          </div>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>
      <el-empty v-else-if="!loading && list.length === 0" description="暂无课程" />

      <div v-else>
        <el-table
          :data="list"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载..."
          row-key="id"
        >
          <el-table-column prop="id" label="ID" width="70" align="center" />
          <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
          <el-table-column label="封面" width="100" align="left">
            <template slot-scope="scope">
              <el-image
                v-if="scope.row.coverImage"
                :src="imgUrl(scope.row.coverImage)"
                :preview-src-list="[imgUrl(scope.row.coverImage)]"
                fit="cover"
                class="cover-thumb"
                title="点击放大"
              />
            </template>
          </el-table-column>
          <el-table-column label="课件" min-width="140" align="left">
            <template slot-scope="scope">
              <a
                v-if="scope.row.fileUrl"
                :href="imgUrl(scope.row.fileUrl)"
                target="_blank"
                rel="noopener"
                class="file-link"
              >查看{{ fileTypeLabel(scope.row.fileUrl) }}</a>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="learnCount" label="学过人数" width="100" align="center" />
          <el-table-column label="状态" width="90" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 1" type="success" size="small">启用</el-tag>
              <el-tag v-else type="info" size="small">禁用</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="160" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="openEdit(scope.row)">编辑</el-button>
              <el-button type="text" size="small" class="danger-text" @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>

    <el-dialog
      :title="isEdit ? '编辑课程' : '新增课程'"
      :visible.sync="dialogVisible"
      width="560px"
      append-to-body
      @closed="onDialogClosed"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="标题" prop="title">
          <el-input v-model.trim="form.title" maxlength="100" placeholder="请输入标题" />
        </el-form-item>
        <el-form-item label="封面图片" required>
          <el-upload
            class="upload-box"
            action="#"
            list-type="picture-card"
            :auto-upload="false"
            :limit="1"
            :file-list="coverFileList"
            accept="image/jpeg,image/png,image/gif,image/webp"
            :on-change="onCoverChange"
            :on-remove="onCoverRemove"
            :on-exceed="() => $message.warning('只能上传一张封面')"
            :before-upload="beforeCoverUpload"
          >
            <i v-if="coverFileList.length === 0" class="el-icon-plus"></i>
          </el-upload>
          <div class="form-tip">jpg / png / gif / webp，不超过 2MB</div>
        </el-form-item>
        <el-form-item label="课件文件" required>
          <el-upload
            action="#"
            :auto-upload="false"
            :limit="1"
            :file-list="docFileList"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            :on-change="onDocChange"
            :on-remove="onDocRemove"
            :on-exceed="() => $message.warning('只能上传一个课件')"
            :before-upload="beforeDocUpload"
          >
            <el-button size="small" type="primary" icon="el-icon-upload2">选择文件</el-button>
            <div slot="tip" class="form-tip">支持 PDF / Word（.doc .docx），不超过 50MB</div>
          </el-upload>
          <a
            v-if="isEdit && existingFileUrl && !docFile"
            :href="imgUrl(existingFileUrl)"
            target="_blank"
            rel="noopener"
            class="file-link"
          >当前课件：点击查看</a>
        </el-form-item>
        <el-form-item label="学过人数" prop="learnCount">
          <el-input-number v-model="form.learnCount" :min="0" :step="1" controls-position="right" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="form.status"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { get, del, BASE_URL, IMG_BASE_URL, getResponseMessage, getErrorMessage } from '@/utils/http.js'

const DOC_EXTS = ['.pdf', '.doc', '.docx']

export default {
  name: 'NewbieLessonManagement',
  data() {
    return {
      loading: false,
      submitting: false,
      error: '',
      list: [],
      dialogVisible: false,
      isEdit: false,
      editId: null,
      coverFile: null,
      docFile: null,
      coverFileList: [],
      docFileList: [],
      existingCoverUrl: '',
      existingFileUrl: '',
      form: this.emptyForm(),
      rules: {
        title: [{ required: true, message: '请输入标题', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    emptyForm() {
      return {
        title: '',
        learnCount: 0,
        status: 1
      }
    },
    imgUrl(path) {
      if (!path) return ''
      return path.startsWith('http') ? path : IMG_BASE_URL + path
    },
    fileTypeLabel(url) {
      const lower = (url || '').toLowerCase()
      if (lower.endsWith('.pdf')) return ' PDF'
      if (lower.endsWith('.docx') || lower.endsWith('.doc')) return ' Word'
      return ''
    },
    isDocFile(file) {
      const name = ((file && file.name) || '').toLowerCase()
      return DOC_EXTS.some(ext => name.endsWith(ext))
    },
    async fetchList() {
      this.loading = true
      this.error = ''
      try {
        const result = await get('/admin/newbie-lesson/list')
        this.list = result.data || []
      } catch (e) {
        this.list = []
        this.error = e && e.message ? e.message : '加载失败'
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.isEdit = false
      this.editId = null
      this.form = this.emptyForm()
      this.coverFile = null
      this.docFile = null
      this.coverFileList = []
      this.docFileList = []
      this.existingCoverUrl = ''
      this.existingFileUrl = ''
      this.dialogVisible = true
    },
    openEdit(row) {
      this.isEdit = true
      this.editId = row.id
      this.form = {
        title: row.title || '',
        learnCount: row.learnCount != null ? row.learnCount : 0,
        status: row.status === 0 ? 0 : 1
      }
      this.coverFile = null
      this.docFile = null
      this.existingCoverUrl = row.coverImage || ''
      this.existingFileUrl = row.fileUrl || ''
      this.coverFileList = row.coverImage
        ? [{ name: 'cover', url: this.imgUrl(row.coverImage) }]
        : []
      this.docFileList = row.fileUrl
        ? [{ name: this.fileNameFromUrl(row.fileUrl), url: this.imgUrl(row.fileUrl) }]
        : []
      this.dialogVisible = true
    },
    fileNameFromUrl(url) {
      const path = (url || '').split('?')[0]
      const name = path.substring(path.lastIndexOf('/') + 1)
      return name || '课件'
    },
    onDialogClosed() {
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate()
      }
    },
    beforeCoverUpload(file) {
      if (!(file.type && file.type.startsWith('image/'))) {
        this.$message.error('封面必须是图片')
        return false
      }
      if (file.size / 1024 / 1024 >= 2) {
        this.$message.error('封面不能超过 2MB')
        return false
      }
      return true
    },
    onCoverChange(file, fileList) {
      if (file.raw && file.raw.type && !file.raw.type.startsWith('image/')) {
        this.$message.error('封面必须是图片')
        this.coverFileList = []
        this.coverFile = null
        return
      }
      if (file.raw && file.raw.size / 1024 / 1024 >= 2) {
        this.$message.error('封面不能超过 2MB')
        this.coverFileList = []
        this.coverFile = null
        return
      }
      this.coverFile = file.raw
      this.coverFileList = fileList.slice(-1)
    },
    onCoverRemove() {
      this.coverFile = null
      this.coverFileList = []
      this.existingCoverUrl = ''
    },
    beforeDocUpload(file) {
      if (!this.isDocFile(file)) {
        this.$message.error('只能上传 PDF 或 Word（.doc / .docx）')
        return false
      }
      if (file.size / 1024 / 1024 >= 50) {
        this.$message.error('课件不能超过 50MB')
        return false
      }
      return true
    },
    onDocChange(file, fileList) {
      if (!this.isDocFile(file)) {
        this.$message.error('只能上传 PDF 或 Word（.doc / .docx）')
        this.docFileList = []
        this.docFile = null
        return
      }
      if (file.raw && file.raw.size / 1024 / 1024 >= 50) {
        this.$message.error('课件不能超过 50MB')
        this.docFileList = []
        this.docFile = null
        return
      }
      this.docFile = file.raw
      this.docFileList = fileList.slice(-1)
    },
    onDocRemove() {
      this.docFile = null
      this.docFileList = []
      this.existingFileUrl = ''
    },
    async handleSubmit() {
      if (!this.$refs.formRef) return
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        if (!this.isEdit && !this.coverFile) {
          this.$message.error('请上传封面图片')
          return
        }
        if (!this.isEdit && !this.docFile) {
          this.$message.error('请上传课件（PDF / Word）')
          return
        }
        if (this.isEdit && !this.coverFile && !this.existingCoverUrl) {
          this.$message.error('请上传封面图片')
          return
        }
        if (this.isEdit && !this.docFile && !this.existingFileUrl) {
          this.$message.error('请上传课件（PDF / Word）')
          return
        }

        this.submitting = true
        try {
          const formData = new FormData()
          formData.append('title', this.form.title)
          formData.append('learnCount', String(this.form.learnCount != null ? this.form.learnCount : 0))
          formData.append('status', String(this.form.status))
          if (this.coverFile) {
            formData.append('coverFile', this.coverFile)
          }
          if (this.docFile) {
            formData.append('file', this.docFile)
          }

          const token = localStorage.getItem('token')
          const url = this.isEdit
            ? `${BASE_URL}/admin/newbie-lesson/${this.editId}`
            : `${BASE_URL}/admin/newbie-lesson`
          const response = await fetch(url, {
            method: this.isEdit ? 'PUT' : 'POST',
            headers: {
              ...(token && { token })
            },
            body: formData
          })
          const result = await response.json()
          if (!(result.code === 200 || result.code === 1)) {
            throw new Error(getErrorMessage(result, '保存失败'))
          }
          this.dialogVisible = false
          this.$message.success(getResponseMessage(result, this.isEdit ? '编辑成功' : '添加成功'))
          this.fetchList()
        } catch (e) {
          this.$message.error(getErrorMessage(e, '保存失败'))
        } finally {
          this.submitting = false
        }
      })
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`确定删除「${row.title}」吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const result = await del(`/admin/newbie-lesson/${row.id}`)
        this.$message.success(getResponseMessage(result, '删除成功'))
        this.fetchList()
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(getErrorMessage(e, '删除失败'))
        }
      }
    }
  }
}
</script>

<style scoped>
.filter-card {
  margin-bottom: 16px;
}

.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.hint {
  color: #606266;
  font-size: 14px;
}

.error {
  color: #f87171;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  padding: 8px 10px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.cover-thumb {
  width: 48px;
  height: 48px;
  border-radius: 4px;
  cursor: pointer;
  vertical-align: middle;
}

.file-link {
  color: #409eff;
  text-decoration: none;
}

.file-link:hover {
  text-decoration: underline;
}

.danger-text {
  color: #f56c6c;
}

.form-tip {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}

.upload-box >>> .el-upload--picture-card,
.upload-box >>> .el-upload-list--picture-card .el-upload-list__item {
  width: 100px;
  height: 100px;
  line-height: 100px;
}
</style>
